
import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TaskLogEntry {
  id: string;
  task_id: string;
  action_type: 'created' | 'completed' | 'deleted';
  performed_by: string;
  performed_by_email: string;
  performed_at: string;
  scheduled_day: string | null;
  scheduled_time: string | null;
  task_data: {
    title: string;
    description?: string;
    hebrew_title?: string;
    hebrew_description?: string;
  };
}

interface TaskLogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TaskLog = ({ open, onOpenChange }: TaskLogProps) => {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['taskLogs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_logs')
        .select('*')
        .order('performed_at', { ascending: false });

      if (error) throw error;
      return data as TaskLogEntry[];
    },
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Task Activity Log</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    Loading logs...
                  </TableCell>
                </TableRow>
              ) : logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    {format(new Date(log.performed_at), 'MMM d, yyyy HH:mm')}
                  </TableCell>
                  <TableCell className="capitalize">
                    {log.action_type}
                  </TableCell>
                  <TableCell>
                    {log.task_data.title}
                    {log.task_data.hebrew_title && (
                      <div className="text-sm text-gray-500">
                        {log.task_data.hebrew_title}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.task_data.description && (
                      <div className="text-sm">
                        {log.task_data.description}
                      </div>
                    )}
                    {log.task_data.hebrew_description && (
                      <div className="text-sm text-gray-500">
                        {log.task_data.hebrew_description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.scheduled_day && (
                      <div>
                        {log.scheduled_day}
                        {log.scheduled_time && (
                          <div className="text-sm text-gray-500">
                            at {log.scheduled_time}
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-500">
                      {log.performed_by_email}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
